import { useState } from 'react';
import { useQuery } from '@tanstack/react-query';
import { formatDistanceToNow } from 'date-fns';
import { apiGet } from '../../api/client.js';

/**
 * Owner/admin-only audit log view. Lists rows from the audit_log table
 * with sensible labels for common actions and a Load more pager.
 */
const ACTION_LABEL = {
  'social_account.connected':    'Connected social account',
  'social_account.disconnected': 'Disconnected social account',
  'project.auto_publish_toggled': 'Toggled auto-publish',
  'member.added':                'Added team member',
  'member.role_changed':         'Changed member role',
  'member.removed':              'Removed member',
  'plan.regenerated':            'Regenerated plan',
};

function fmtAgo(iso) {
  try { return formatDistanceToNow(new Date(iso), { addSuffix: true }); }
  catch { return iso; }
}

export default function AuditLog() {
  const [limit, setLimit] = useState(50);

  const q = useQuery({
    queryKey: ['audit-log', limit],
    queryFn: ({ signal }) => apiGet(`/audit-log?limit=${limit}`, { signal }),
  });

  return (
    <div className="account-page">
      <header className="account-page-head">
        <h1>Audit log</h1>
        <p className="muted">
          Every privileged action on this organization: social connections,
          team membership changes, plan regenerations, auto-publish toggles.
          Read-only and append-only — entries can’t be edited or deleted.
        </p>
      </header>

      <section className="account-section">
        {q.isLoading && <div className="loading">Loading audit log…</div>}
        {q.isError && <div className="error">Couldn’t load audit log: {q.error.message}</div>}

        {q.data && q.data.entries.length === 0 && (
          <div className="empty">No audit entries yet. Privileged actions get logged here automatically.</div>
        )}

        {q.data && q.data.entries.length > 0 && (
          <>
            <ol className="audit-list">
              {q.data.entries.map((e) => (
                <li key={e.id} className="audit-row">
                  <span className="audit-when">{fmtAgo(e.created_at)}</span>
                  <span className="audit-action">{ACTION_LABEL[e.action] || e.action}</span>
                  <span className="audit-actor">{e.actor_email || e.actor_user_id?.slice(0, 8) || 'system'}</span>
                  <span className="audit-target muted small">
                    {e.target_type}{e.target_id ? ` · ${e.target_id.slice(0, 8)}…` : ''}
                  </span>
                  {e.detail && Object.keys(e.detail).length > 0 && (
                    <details className="audit-detail">
                      <summary>detail</summary>
                      <pre>{JSON.stringify(e.detail, null, 2)}</pre>
                    </details>
                  )}
                </li>
              ))}
            </ol>
            {q.data.entries.length >= limit && (
              <button type="button" className="btn-compact" onClick={() => setLimit(limit + 50)}>
                Load 50 more
              </button>
            )}
          </>
        )}
      </section>
    </div>
  );
}
